//IMPORTANT NOTE : IF CREATING A NEW COMPONENT/STATE SLICES
// (OR EDITING OLD ONES), new selectors belong in "selectors" folder
import { BehaviorSubject } from 'rxjs'
import { FRONT, RIGHT, LEFT, BACKWARD, FORWARD, ENTRY_POINT_BEFORE } from 'aria-viewer/navigation/navigationActions'
import {
    AUDIO_PLAY,
    AUDIO_STOP,
    AUDIO_MAIN,
    AUDIO_TOUR,
    AUDIO_PM,
    AUDIO_SUPP,
    AUDIO_HS,
    AUDIO_TRIGGER,
    AUDIO_LOAD,
} from 'aria-viewer/audiomanager/audioManagerActions'
import { createSelector } from 'reselect'
import pickBy from 'lodash-es/pickBy'
import isUndefined from 'lodash-es/isUndefined'
import negate from 'lodash-es/negate'
import isEmpty from 'lodash-es/isEmpty'
import { MEDIA_TYPES, MEDIA_PROPS_LIST, TABS, TOUR_TYPES } from 'aria-viewer/tour/constants'
import ModalContentTypes from 'aria-viewer/modal/constants/ModalContentTypes'
import { HIDE_DRAWER, MAP_DRAWER, TOUR_DRAWER } from 'aria-viewer/drawer/drawerMenuActions'
import MobileDetect from 'aria-viewer/utils/mobileDetect'
import { Map } from 'immutable'
import SessionStorage from 'aria-viewer/utils/sessionStorage'
import UriHandler from 'aria-viewer/utils/uriHandler'
import { FROM_VR, WELCOME_PROMPT_HIDDEN } from 'aria-viewer/tour/constants/queryConstants'
import { transformMedia, internalGetMainMedia } from 'aria-viewer/media/mediaTransformer'
import getters from './selectorGetters'
import { getEnv } from 'aria-viewer/utils/environment'
import getBool from 'aria-viewer/utils/getBool'

const defaultValue = Map({
    value: 'off',
    display: 'Off',
})

const ordinal = {
    flat: 1,
    '360': -1,
}

const SUBJECT_HANDLER = () => {
    const subject$ = new BehaviorSubject()
    return value => {
        setTimeout(() => subject$.next(value))
        return subject$
    }
}

const VALUE_HANDLER = () => value => value

const withState = (value, handler = VALUE_HANDLER) => {
    const selector = createSelector(
        value,
        handler()
    )
    return () => selector
}

const withProps = (value, handler) => () => withState(value, handler)()

const memoize = (array, handler) =>
    createSelector(
        array,
        handler
    )

//HELPER
//TODO : MAKE THIS BETTER
const pointInfoCache = {}
const getPointInfo = (language, navigation, point) => {
    const { descriptions, dir, stopid } = point
    const orientation = navigation.get('orientation')

    const current = language.get('current') || {}
    const current_language = current.get('language')
    const details =
        descriptions && descriptions[current_language] && descriptions[current_language][orientation]
            ? descriptions[current_language][orientation]
            : {}

    const { description = '', avatar_url = '', audio_url = '' } = details
    const key = `${dir}_${stopid}_${orientation}_${current_language}`

    return (pointInfoCache[key] = pointInfoCache[key] || {
        language: current,
        avatar_url: encodeURI(UriHandler.fixProtocol(avatar_url)),
        description,
        dir,
        stopid,
        audio_url,
    })
}

// getters

const metaModels = state => state.getIn(['meta', 'models'])
const language = state => state.get('language')
const navigation = state => state.get('navigation')
const contextMenu = state => state.get('contextMenu')
const modal = state => state.get('modal')
const drawer = state => state.get('drawerMenu').get('name')
const stopCardsState = state => state.get('stopCards').get('state')
const audioManager = state => state.get('audioManager')

const drawerRaw = state => state.get('drawerMenu').toJSON()

// TODO: get rid of these aggregated selectors requiring a component's own props
// TODO: they're not efficient and make it difficult to troubleshoot/envision logic
const getMyIndex = (state, props) => props.index
const getMediaIndex = (state, props) => (props.mediaIndex === undefined ? 1 : props.mediaIndex)
const getParams = (state, props) => props.match.params
const getLocation = (state, props) => props.location

//selectors
const location = memoize(
    [getters.tour.locationsList, getters.tour.locationIndex],
    (locationsList, locationIndex) => locationsList[locationIndex] || {}
)
const locationName = memoize([location], location => location.name || '')

export const tour = memoize(
    [getters.tour.toursList, getters.tour.tourid, location],
    (toursList, tourid, location) =>
        toursList.find(tour => tour.tourid === tourid && tour.loc_id === location.loc_id) || toursList[tourid] || {}
)

//this is an array of stopids that the introvideo already played
const isStopIntroPlayed = state => state.getIn(['tour', 'stops', 'isStopIntroPlayed'])

const hasIntroVideoPlayed = memoize(
    [getters.tour.stopIndex, getters.tour.stopsList, isStopIntroPlayed],
    (stopIndex, stopsList, isStopIntroPlayed) => {
        const currentStop = stopsList.get(stopIndex) && stopsList.get(stopIndex)['stopid']
        return isStopIntroPlayed.get(currentStop)
    }
)

const isMapEnabled = memoize([tour], tour => !tour.hide_map, 'isMapEnabled')
const isWalkingtour = memoize([tour], tour => tour.type == TOUR_TYPES.walkingtour)
const isHybridtour = memoize([tour], tour => tour.type == TOUR_TYPES.hybridtour)
const isMediatour = memoize([tour], tour => tour.type == TOUR_TYPES.mediatour)
export const canDeriveMediaFromStops = memoize(
    [isHybridtour, isMediatour],
    (isHybridtour, isMediatour) => isHybridtour || isMediatour
)
const isVrModuleEnabled = memoize([location, getters.tour.stopsList], (location, stops) => {
    if (location.vrModuleEnabled) {
        const stopsWithVrMainMedia = stops.filter(stop => {
            return (
                stop.main_media_type == MEDIA_TYPES.panorama
                // uncomment below if you want to check for 360 videos too
                // || stop.main_media_type == MEDIA_TYPES.video360
            )
        })

        // if there are no stops with main media as pano, disable vrmodule
        return stopsWithVrMainMedia.size > 0
    } else {
        return false
    }
})

const isInteractiveMap = memoize([getters.map.mapData, tour], (mapData, tour) => {
    if (mapData && mapData.stops) {
        return (
            tour.fullmap &&
            mapData.stops.reduce((list, stop) => {
                if (stop.shape) {
                    return [...list, stop.title]
                } else {
                    return list
                }
            }, []).length > 0
        )
    }
})

const hasLocations = memoize([getters.tour.locationsList], locationsList => locationsList && locationsList.length > 0)
const loading = memoize([getters.tour.tourLoading], tourLoading =>
    tourLoading.map(value => value).reduce((total, value) => total || !!value, false)
)

const trail = memoize(
    [getters.tour.trailsList, getters.tour.trailIndex],
    (trailsList, trailIndex) => trailsList[trailIndex] || {}
)
const pointsList = memoize([trail, getters.tour.pointsRawList], (trail, pointsRawList) => pointsRawList[trail.id] || [])
const point = memoize([pointsList, getters.tour.pointIndex], (pointsList, pointIndex) => pointsList[pointIndex] || {})
export const pointInfo = memoize([language, navigation, point], getPointInfo)
const audioUrl = memoize([pointInfo], pointInfo => pointInfo.audio_url || '')
const avatarUrl = memoize([pointInfo], pointInfo => pointInfo.avatar_url || '')
const trail_last_point = memoize(
    [pointsList, getters.tour.pointIndex],
    (pointsList, pointIndex) => pointsList.length === pointIndex + 1
)
const trailsById = memoize([getters.tour.trailsList], trailsList =>
    trailsList.reduce(
        (trails, trail) =>
            Object.assign({
                ...trails,
                [trail.trailid]: trail,
            }),
        {}
    )
)

const stopsToPoints = memoize(
    [getters.tour.stopsList, getters.tour.trailsList, getters.tour.pointsRawList, language],
    (stopsList, trailsList, pointsRawList, language) => {
        const navigation = Map({
            orientation: FRONT,
        })

        //For each Stop
        return stopsList
            .map(stop => {
                //Check the list of trails
                return trailsList.reduce((trailPair, trail) => {
                    //If we have points for this trail
                    return (pointsRawList[trail.id] || []).reduce((pointPair, point) => {
                        //If the point belongs to this stop return it's info
                        if (point.stopid === stop.stopid && point.main) {
                            return Object.assign({}, getPointInfo(language, navigation, point), {
                                title: stop.title || '',
                            })
                        } else {
                            return pointPair
                        }
                    }, trailPair)
                }, {})
            })
            .toJSON()
    }
)

const registrationForm = memoize([location], location => location.registrationForm || {})

export const currentStop = memoize(
    [getters.tour.stopsList, getters.tour.stopIndex],
    (stopsList, stopIndex) => stopsList.get(stopIndex) || {}
)

export const mediaListByType = memoize(
    [
        getters.tour.stopsList,
        getters.supplementalMedia.mediaStopId,
        getters.supplementalMedia.supplementalMediaShowAll,
        stopsToPoints,
        canDeriveMediaFromStops,
        currentStop,
    ],
    (stopsList, mediaStopId, supplementalMediaShowAll, stopsToPoints, canDeriveMediaFromStops, currentStop) => {
        const filter = (medias, [main = {}]) =>
            medias.filter(media => media.id !== main.id || media.mediaType !== main.mediaType)

        return stopsList.reduce(
            (mediaList, stop, index) => {
                if (stop.stopid === currentStop.stopid || supplementalMediaShowAll) {
                    const mainMedia =
                        currentStop.stopid === stop.stopid
                            ? [
                                  internalGetMainMedia({
                                      stop,
                                      point: stopsToPoints[index],
                                      orientation: FRONT,
                                      canDeriveMediaFromStops,
                                  }),
                              ]
                            : []
                    const photos = stop.photos.map(media => transformMedia({ media }))
                    const panoramas = stop.panoramas.map(media => transformMedia({ media }))
                    const videos = stop.videos.map(media => transformMedia({ media }))
                    return {
                        mainMedia: [...mediaList.mainMedia, ...mainMedia],
                        photos: [...mediaList.photos, ...filter(photos, mainMedia)],
                        panoramas: [...mediaList.panoramas, ...filter(panoramas, mainMedia)],
                        videos: [...mediaList.videos, ...filter(videos, mainMedia)].sort(
                            (a, b) => ordinal[a.playback_type] - ordinal[b.playback_type]
                        ),
                    }
                }
                return mediaList
            },
            {
                mainMedia: [],
                photos: [],
                panoramas: [],
                videos: [],
            }
        )
    }
)

const locationSelectorList = memoize(
    [getters.tour.locationsList, getters.tour.toursList],
    (locationsList, toursList) => {
        return locationsList.length > 1
            ? locationsList
                  .reduce((list, location) => {
                      const toursForLocation = toursList.reduce((tours, tour) => {
                          if (tour.loc_id == location.loc_id) {
                              return [
                                  ...tours,
                                  {
                                      private:
                                          location.privacy === 'private' ||
                                          location.status !== 'live' ||
                                          tour.status !== 'live',
                                      name: location.name,
                                      profileImage: tour.profileImage,
                                      tourid: tour.tourid,
                                      inst_unique_id: location.inst_unique_id,
                                      loc_id: location.loc_id,
                                  },
                              ]
                          }
                          return [...tours]
                      }, [])

                      return [...list, ...toursForLocation]
                  }, [])
                  .filter(tour => !tour.private)
            : toursList.filter(tour => tour.status === 'live')
    }
)
const horizontalView = memoize([mediaListByType], mediaListByType =>
    MEDIA_PROPS_LIST.reduce((mediaList, type) => [...mediaList, ...mediaListByType[type]], [])
)

const supplementalMedia = memoize(
    [getters.supplementalMedia.media, horizontalView, getters.supplementalMedia.mediaStopId],
    (media, horizontalView, mediaStopId) => {
        return transformMedia({
            media:
                (media && (!mediaStopId || mediaStopId === media.get('stopid'))
                    ? media.toObject()
                    : horizontalView[0]) || {},
        })
    }
)

const supplementalMediaCarousel = memoize(
    [getters.tour.stopsList, stopsToPoints, canDeriveMediaFromStops],
    (stopsList, stopsToPoints, canDeriveMediaFromStops) => {
        return stopsList.map((stop, index) => {
            const mainMedia = internalGetMainMedia({
                stop,
                point: stopsToPoints[index],
                orientation: FRONT,
                canDeriveMediaFromStops,
            })
            let medias = MEDIA_PROPS_LIST.reduce(
                (medias, mediaType) => [
                    ...medias,
                    ...stop[mediaType].filter(media => {
                        if (media.mediaType == 'video' && media.id == stop.intro_media) {
                            return false
                        }
                        return media.id != mainMedia.id || media.mediaType !== mainMedia.mediaType
                    }),
                ],
                []
            )

            //Supplemental media carousels only need up to 4 media + mainMedia as the first index
            //process the slices into useable media
            const count = medias.length
            medias = medias.slice(0, 4).map(item => transformMedia({ media: item }))

            //this will sort the media based on priority, if priority is 0, put it in the back
            medias.sort((a, b) => {
                if (a.priority < b.priority || b.priority == 0) {
                    return -1
                }
                if (a.priority > b.priority) {
                    return 1
                }
                return 0
            })

            // Add main media to the begining of the array
            medias.unshift(mainMedia)

            return {
                count,
                medias,
                locId: stop.loc_id,
            }
        })
    }
)

const lengthCache = {} //TODO MAKE THIS CACHING BETTER
const supplementalMediaCarouselMediasLength = memoize(
    [supplementalMediaCarousel, getMyIndex],
    (supplementalMediaCarousel, index) => {
        const item = supplementalMediaCarousel.get(index) || { medias: [], count: 0 }
        const mediaSize = item.medias.length
        const mediaCount = item.count
        const key = `${mediaSize}_${mediaCount}`
        return (lengthCache[key] = lengthCache[key] || { mediaSize, mediaCount })
    }
)

const supplementalMediaCarouselMediaByIndex = memoize(
    [supplementalMediaCarousel, getMyIndex, getMediaIndex],
    (supplementalMediaCarousel, index, mediaIndex) => {
        const item = supplementalMediaCarousel.get(index) || { medias: [] }
        const itemAtIndex = item.medias[mediaIndex] || {}

        //Walking tur profile images dont have a loc_id or id
        //Do not attempt to transform these media
        if (itemAtIndex.loc_id && itemAtIndex.id) {
            return transformMedia({ media: itemAtIndex })
        } else {
            return itemAtIndex
        }
    }
)

const tab = memoize(
    [getters.supplementalMedia.supplementalMediaTab, supplementalMedia],
    (tab, supplementalMedia) =>
        tab ||
        {
            panorama: TABS.panos,
            video: TABS.videos,
            photo: TABS.photos,
        }[supplementalMedia.mediaType],
    'tab'
)

// what is this doing?
const languages = memoize([location], location =>
    (location.languages || []).reduce(
        (list, value) => [
            ...list,
            Map(value).merge({
                value: value.language.toLowerCase(),
                display: value.language,
            }),
        ],
        []
    )
)

export const hasNonEnglishLangugages = memoize(
    [languages],
    languages => languages.filter(language => language.get('value') !== 'english').length > 0
)

const mediaIndex = memoize([supplementalMedia, horizontalView], (supplementalMedia, horizontalView) =>
    horizontalView.findIndex(
        media => media.id === supplementalMedia.id && media.mediaType === supplementalMedia.mediaType
    )
)
const audioLanguage = memoize([language, audioManager], (language, audioManager) =>
    audioManager.getIn(['state', AUDIO_TOUR]) != AUDIO_STOP ? language : defaultValue
)

const languagesAudio = memoize([languages], languages => [defaultValue, ...languages])
export const selectedLanguage = memoize([languages, language], (languages, language) =>
    languages.reduce(
        (selected, value) => (language.get('current').get('language') == value.get('language') ? value : selected),
        {}
    )
)
const selectedAudioLanguage = memoize([audioLanguage, languagesAudio], (audioLanguage, languagesAudio) =>
    languagesAudio.reduce(
        (selected, value) => (audioLanguage.getIn(['current', 'language']) == value.get('language') ? value : selected),
        defaultValue
    )
)

const isCurrentStop = memoize(
    [getters.tour.stopIndex, getMyIndex],
    (currentStop, stopCardIndex) => stopCardIndex === currentStop
)

//This represents that there are muiltiple
//languages in text but only one language
//in audio
const isSingleAudioLanguage = memoize([point, navigation], (point, navigation) => {
    if (isEmpty(point)) return false
    const orientation = navigation.get('orientation')
    const languagesWithAudioUrl = Object.values(point.descriptions).filter(item => {
        return item[orientation] && item[orientation].audio_url != ''
    })
    return languagesWithAudioUrl.length == 1
})

const isAudioButtonVisible = memoize([audioUrl, avatarUrl, languages], (audioUrl, avatarUrl, languages) => {
    return languages.length > 0 || (avatarUrl != '' || audioUrl != '')
})

export const mainMedia = memoize(
    [
        currentStop,
        pointInfo,
        point,
        getters.navigation.orientation,
        getters.tour.altMainMedia,
        canDeriveMediaFromStops,
        getters.tour.skippedAltMainMedia,
        metaModels,
        isStopIntroPlayed,
        getters.device.isGyroscopeEnabled,
    ],
    (
        currentStop,
        pointInfo,
        point,
        orientation,
        altMainMedia,
        canDeriveMediaFromStops,
        skippedAltMainMedia,
        metaModels,
        isStopIntroPlayed,
        isGyroscopeEnabled
    ) => {
        // If it is a Media Tour make sure that current stop is populated
        if (currentStop == null || Object.keys(currentStop).length == 0) {
            return {}
        }

        //No valid point yet
        if (Object.keys(point).length == 0) {
            return {}
        }

        if (altMainMedia) {
            return transformMedia({
                media: altMainMedia,
                mainMedia: true,
                metaModels,
                enableAccelerometer: isGyroscopeEnabled,
            })
        } else if (currentStop.intro_media && !altMainMedia && !isStopIntroPlayed.get(currentStop.stopid)) {
            if (skippedAltMainMedia || !point.main) {
                return internalGetMainMedia({
                    stop: currentStop,
                    point: pointInfo,
                    orientation,
                    canDeriveMediaFromStops,
                    mainMedia: true,
                    metaModels,
                    enableAccelerometer: isGyroscopeEnabled,
                })
            } else {
                return {}
            }
        }

        return internalGetMainMedia({
            stop: currentStop,
            point: pointInfo,
            orientation,
            canDeriveMediaFromStops,
            mainMedia: true,
            metaModels,
            enableAccelerometer: isGyroscopeEnabled,
        })
    }
)

const mainMediaType = memoize([mainMedia], mainMedia => mainMedia.mediaType)

const mainMediaPlaybackType = memoize([mainMedia], mainMedia => mainMedia.type)

export const mainMediaHasPaymentHotspots = memoize([mainMedia], mainMedia => {
    const { hotspots = [] } = mainMedia

    return hotspots.some(hotspot => ![null, undefined].includes(hotspot.payments_product_id))
})

const hasMultipleTrails = memoize([getters.tour.trailsList], trailsList => trailsList.length > 0)

const hasMultipleStops = memoize([getters.tour.stopsList], stopsList => stopsList.size > 1)

export const hasMultipleToursLocations = memoize(
    [locationSelectorList],
    locationSelectorList => locationSelectorList.length > 1
)

const mapTourLabel = memoize([getters.tour.toursList, getters.map.mapData], (toursList, mapData) => {
    const tourNames = toursList.map(tour => tour.name)
    const trailNames = mapData ? mapData.trails.map(trail => trail.name) : []

    const matches = tourNames.reduce((matches, name) => (trailNames.indexOf(name) >= 0 ? matches + 1 : matches), 0)

    return matches > 0 ? 'Tours' : 'Trails'
})

const hasMainMedia = memoize([currentStop], currentStop => currentStop.main_media_id != null)

const trailConnections = memoize([trail], trail => trail.connections || [])

const endOfTourMessage = memoize([tour], tour => (tour.end_tour_message ? tour.end_tour_message : null))

const isFacingForward = memoize([getters.navigation.orientation], orientation => orientation == FRONT)

const navButtons = memoize(
    [
        trail_last_point,
        isFacingForward,
        trailConnections,
        point,
        trailsById,
        selectedLanguage,
        pointsList,
        getters.tour.pointIndex,
        getters.tour.trailIndex,
        getters.tour.trailsList,
        getters.tour.stopsList,
        getters.tour.stopIndex,
        endOfTourMessage,
        hasMainMedia,
        mainMediaType,
        isMediatour,
        isHybridtour,
    ],
    (
        trail_last_point,
        isFacingForward,
        trailConnections,
        point,
        trailsById,
        selectedLanguage,
        pointsList,
        pointIndex,
        trailIndex,
        trailsList,
        stopsList,
        stopIndex,
        endOfTourMessage,
        hasMainMedia,
        mainMediaType,
        isMediatour,
        isHybridtour
    ) => {
        if (isMediatour) {
            return []
        }
        let buttons = []
        let nextTrail = null
        let isPhone = MobileDetect.phone()
        //Get Arrows
        if (trail_last_point) {
            //Handle point on the trail
            if (trailConnections.length > 0) {
                buttons = [
                    ...buttons,
                    ...trailConnections.reduce((connections, item) => {
                        if (item.connection == 'END') {
                            nextTrail = trailsById[item.destTrailid] || trailsList[trailIndex + 1]

                            if (!nextTrail) {
                                return connections
                            }
                            return [
                                ...connections,
                                {
                                    type: 'hint',
                                    actionName: 'gotoTrailFirstPoint',
                                    trail: nextTrail,
                                    direction: RIGHT,
                                    title: nextTrail.name,
                                },
                            ]
                        } else if (item.connection != 'B') {
                            return [
                                ...connections,
                                {
                                    type: 'arrow',
                                    actionName: 'gotoTrailFirstPoint',
                                    trail: trailsById[item.destTrailid],
                                    direction: item.connection,
                                },
                            ]
                        }
                        return connections
                    }, []),
                ]

                // Build Back Button
                if (!endOfTourMessage) {
                    const backConnections = trailsList[trailIndex].connections.reduce((backConnectsions, conn) => {
                        if ('B' == conn.connection) return [...backConnectsions, conn]
                        else return backConnectsions
                    }, [])

                    /**
                     * Should we loop to the begining of the experience ?
                     */
                    if (nextTrail == null && pointIndex + 1 == pointsList.length) {
                        nextTrail =
                            trailIndex + 1 == trailsList.length && !endOfTourMessage
                                ? trailsList[0]
                                : trailsList[trailIndex + 1]
                        //if we can find a nextTrail and have no explicit nav lets loop
                        if (nextTrail && buttons.length == 0) {
                            buttons = [
                                ...buttons,
                                {
                                    type: 'hint',
                                    actionName: 'gotoTrailFirstPoint',
                                    direction: RIGHT,
                                    trail: nextTrail,
                                    title: nextTrail.name,
                                },
                            ]
                        }
                    } else {
                        /**
                         * We are mid trail and should have a previous button
                         */
                        buttons = [
                            ...buttons,
                            {
                                type: 'arrow',
                                actionName: 'prevPoint',
                                direction: point.prev_step,
                            },
                        ]
                    }

                    /**
                     * Do we specifically have a back connection?
                     * If so lets add a back button
                     */
                    if (backConnections.length > 0) {
                        buttons = [
                            ...buttons,
                            {
                                type: 'arrow',
                                actionName: 'prevPoint',
                                direction: 'B',
                            },
                        ]
                    }
                }
            } else {
                //don't allow looping if there's no connection in the end
                if (pointIndex != 0) {
                    buttons = [
                        ...buttons,
                        {
                            type: 'hint',
                            actionName: 'prevPoint',
                            direction: isPhone ? 'FRONT' : 'B', // pass in FRONT to get the styling on mobile
                            title: 'Step Back',
                        },
                    ]
                }
                //Allow the user to navigate to the next stop if we're not on the last trail
                if (trailIndex < trailsList.length - 1) {
                    buttons = [
                        ...buttons,
                        {
                            type: 'hint',
                            actionName: 'nextPoint',
                            direction: FRONT,
                            title: stopsList.get(stopIndex + 1).title,
                        },
                    ]
                }
            }
        } else {
            //Handle last trail point
            if (isFacingForward) {
                if (hasMainMedia && point.stopid && mainMediaType != MEDIA_TYPES.photo && isHybridtour) {
                    buttons = [
                        ...buttons,
                        {
                            type: 'hint',
                            actionName: 'nextCoord',
                            direction: FRONT,
                            orientation: FRONT,
                            walkingDirection: FORWARD,
                            mainMediaType: mainMediaType,
                            title: 'Continue to ' + stopsList.get(stopIndex + 1).title,
                        },
                    ]
                } else {
                    let buttonInfo = Object.assign(
                        {},
                        {
                            type: 'arrow',
                            direction: point.next_step,
                            actionName: 'nextPoint',
                        },
                        isHybridtour
                            ? {
                                  actionName:
                                      pointsList.length > pointIndex &&
                                      pointsList[pointIndex + 1 != pointsList.length ? pointIndex + 1 : pointIndex]
                                          .stopid != null
                                          ? 'nextCoord'
                                          : 'nextPoint',
                                  walkingDirection: FORWARD,
                                  mainMediaType: mainMediaType,
                              }
                            : {}
                    )
                    buttons = [...buttons, buttonInfo]
                }

                if (pointIndex > 0 || trailIndex != 0) {
                    const prevTrailId = trailConnections.reduce((prevTrailId, prop) => {
                        if (prop.connection == 'B') {
                            return prop.destTrailid
                        }
                        return prevTrailId
                    }, '')

                    if (
                        (hasMainMedia && point.stopid) ||
                        (pointsList.length > pointIndex &&
                            pointsList[pointIndex - 1 >= 0 ? pointIndex - 1 : pointIndex].stopid != null &&
                            isHybridtour)
                    ) {
                        buttons = [
                            ...buttons,
                            {
                                type: 'arrow',
                                actionName: 'prevCoord',
                                direction: 'B',
                                walkingDirection: BACKWARD,
                                mainMediaType: mainMediaType,
                                trail: trailsById[prevTrailId],
                            },
                        ]
                    } else {
                        buttons = [
                            ...buttons,
                            {
                                type: 'arrow',
                                actionName: prevTrailId && pointIndex == 0 ? 'gotoTrailLastPoint' : 'prevPoint',
                                direction: point.prev_step,
                                trail: trailsById[prevTrailId],
                            },
                        ]
                    }
                }
                const language =
                    (selectedLanguage.get && selectedLanguage.get('language')) || selectedLanguage.language || 'English'
                if (
                    point &&
                    point.orientation &&
                    point.descriptions[language] &&
                    point.descriptions[language][point.orientation] &&
                    (Object.keys(point.descriptions[language]).length > 1 || [RIGHT, LEFT].includes(point.orientation))
                ) {
                    if (isPhone) {
                        buttons = [
                            ...buttons,
                            {
                                type: 'hint',
                                actionName: 'faceDirection',
                                orientation: point.orientation,
                                direction: point.orientation,
                                title: point.descriptions[language][point.orientation].title,
                            },
                        ]
                    } else {
                        buttons = [
                            ...buttons,
                            {
                                type: 'look',
                                actionName: 'faceDirection',
                                orientation: point.orientation,
                                direction: point.orientation,
                                mainMediaType: mainMediaType,
                                title: point.descriptions[language][point.orientation].title,
                                lookThumb: `${point.dir}560_${point.orientation.toLowerCase()}.jpg`,
                            },
                        ]
                    }
                }
            } else {
                buttons = [
                    ...buttons,
                    {
                        type: 'hint',
                        actionName: 'faceDirection',
                        direction: LEFT,
                        orientation: FRONT,
                        title: 'Back To Trail',
                    },
                ]
            }
        }

        //Get Hints
        const hints = point.descriptions ? point.descriptions[selectedLanguage.language] : null
        if (isFacingForward && hints) {
            //Build the hints if we're facing forward
            buttons = [
                ...buttons,
                ...Object.keys(hints).reduce((hintArray, key) => {
                    if (key == RIGHT || key == LEFT) {
                        return [
                            ...hintArray,
                            {
                                type: 'hint',
                                actionName: 'faceDirection',
                                title: hints[key].title,
                                direction: key,
                                orientation: key,
                            },
                        ]
                    }
                    return hintArray
                }, []),
            ]
        }

        return buttons
    }
)

const stopToMarker = ({ point, stop, selectedLanguage, stopMapData = {} }) => {
    const lat = point.enableTourLoc ? point.tourLat : point.latitude
    const lng = point.enableTourLoc ? point.tourLng : point.longitude
    const language =
        selectedLanguage.language || (selectedLanguage.get && selectedLanguage.get('language')) || 'English'
    const { description, audio_url } =
        point.descriptions[language] && point.descriptions[language].FRONT ? point.descriptions[language].FRONT : {}

    return {
        id: stop.stopid,
        college: stop.loc_id,
        title: stop.title,
        fullMapDescription: description,
        audio: audio_url,
        reallat: stopMapData ? stopMapData.reallat || lat : lat,
        reallng: stopMapData ? stopMapData.reallng || lng : lng,
        customlat: lat,
        customlng: lng,
        startLat: stopMapData ? stopMapData.startlat : 0,
        startLng: stopMapData ? stopMapData.startlng : 0,
        main: point.main,

        totalVideos: point.totalVideos,
        videoThumb: point.videoThumb,
        hasVideo: point.totalVideos > 0,

        totalPanos: point.totalPanos,
        panoThumb: point.panoThumb,
        hasPanorama: point.totalPanos > 0,

        totalPhotos: point.totalPhotos,
        photoThumb: point.photoThumb,
        hasPicture: point.totalPhotos > 0,
    }
}

const pointCoords = (point = { latitude: 0, longitude: 0 }) => {
    const lat = point.enableTourLoc ? point.tourLat : point.latitude
    const lng = point.enableTourLoc ? point.tourLng : point.longitude

    return {
        reallat: lat,
        reallng: lng,
        customlat: lat,
        customlng: lng,
    }
}

const mapTrailPoints = memoize(
    [
        getters.tour.trailsList,
        getters.tour.pointsRawList,
        getters.tour.locationsList,
        getters.tour.locationIndex,
        isMediatour,
        trailsById,
    ],
    (trailsList, pointsRawList, locationsList, locationIndex, isMediatour, trailsById) => {
        const showTrailLines = locationsList.length > 0 ? locationsList[locationIndex].show_trail_lines : true //show trail line by default

        return !isMediatour && showTrailLines
            ? trailsList.reduce((pointsForTrail, trail) => {
                  let connections = []
                  let rawTrailPoints
                  let destTrail
                  for (const index in trail.connections) {
                      if (trail.connections[index].connection == 'B') {
                          destTrail = trail.connections[index].destTrailid
                          rawTrailPoints = pointsRawList[trailsById[destTrail].id]
                          connections.push(pointCoords(rawTrailPoints[rawTrailPoints.length - 1]))
                          break
                      }
                  }

                  const tpx = pointsRawList[trail.id].reduce((pointsForStop, point) => {
                      return [...pointsForStop, pointCoords(point)]
                  }, [])

                  return [...pointsForTrail, [...connections, ...tpx]]
              }, [])
            : []
    }
)

const mapStopMarkers = memoize(
    [
        getters.tour.pointsRawList,
        getters.tour.stopsList,
        getters.tour.trailsList,
        selectedLanguage,
        getters.map.mapData,
    ],
    (pointsRawList, stopsList, trailsList, selectedLanguage, mapData) => {
        if (pointsRawList.length == 0) {
            return []
        }

        const points = Object.keys(pointsRawList).reduce((pointList, key) => {
            return {
                ...pointList,
                ...pointsRawList[key].reduce((mainPoints, point) => {
                    if (point.main && point.stopid) {
                        return {
                            ...mainPoints,
                            [point.stopid]: point,
                        }
                    } else {
                        return mainPoints
                    }
                }, {}),
            }
        }, {})
        if (mapData && mapData.stops.length > 0) {
            return MobileDetect.phone()
                ? mapData.stops
                : mapData.stops.reduce((stopsForTrail, currentStop) => {
                      if (stopsList.toJSON().find(stop => stop.stopid == currentStop.id) && points[currentStop.id]) {
                          const { enableTourLoc, tourLat, tourLng, latitude, longitude } = points[currentStop.id]
                          const lat = enableTourLoc ? tourLat : latitude
                          const lng = enableTourLoc ? tourLng : longitude
                          return [
                              ...stopsForTrail,
                              Object.assign({}, currentStop, {
                                  reallat: lat,
                                  reallng: lng,
                                  customlat: lat,
                                  customlng: lng,
                              }),
                          ]
                      } else {
                          return stopsForTrail
                      }
                  }, [])
        } else {
            return stopsList.reduce((stopsForTrail, currentStop) => {
                if (!points[currentStop.stopid]) {
                    return stopsForTrail
                } else {
                    return [
                        ...stopsForTrail,
                        stopToMarker({
                            selectedLanguage,
                            stopMapData: mapData ? mapData.stops.find(stop => stop.id == currentStop.stopid) : null,
                            stop: currentStop,
                            point: points[currentStop.stopid],
                        }),
                    ]
                }
            }, [])
        }
    }
)

const panoCoords = memoize(
    [getters.navigation.entryPoint, getters.navigation.walkingDirection, mainMedia, getters.panorama.panoStartLatLon],
    (entryPoint, walkingDirection, mainMedia, panoStartLatLon) => {
        if (mainMedia.mediaType != MEDIA_TYPES.panorama) return null

        const onEntry = walkingDirection ? false : true

        let direction,
            positionVar = 'start'

        if (walkingDirection === null && entryPoint) {
            direction = entryPoint === ENTRY_POINT_BEFORE ? FORWARD : BACKWARD
        } else {
            direction = walkingDirection
        }

        if (
            (mainMedia.enter_lat !== null && mainMedia.enter_lon !== null) ||
            (mainMedia.exit_lat !== null && mainMedia.exit_lon !== null)
        ) {
            if (onEntry) {
                if (direction == FORWARD) {
                    if (mainMedia.enter_lat !== null && mainMedia.enter_lon !== null) {
                        positionVar = 'enter'
                    }
                } else if (direction == BACKWARD) {
                    if (mainMedia.exit_lat !== null && mainMedia.exit_lon !== null) {
                        positionVar = 'exit'
                    }
                }
            } else {
                if (direction == FORWARD) {
                    if ((mainMedia.exit_lat !== null && mainMedia.exit_lon !== null) || walkingDirection) {
                        positionVar = 'exit'
                    }
                } else if (direction == BACKWARD) {
                    if ((mainMedia.enter_lat !== null && mainMedia.enter_lon !== null) || walkingDirection) {
                        positionVar = 'enter'
                    }
                }
            }
        }

        let coords = {
            lat: mainMedia[positionVar + '_lat'],
            lon: mainMedia[positionVar + '_lon'],
        }

        //panoStartLatLon is default to 0 and 180,
        //if it's a different value that means there's an intro video
        if (panoStartLatLon.lat != 0 && panoStartLatLon.lon != 180) {
            coords = {
                lat: panoStartLatLon.lat,
                lon: panoStartLatLon.lon,
            }
        }

        return {
            lat: coords.lat,
            lon: coords.lon,
            walkingDirection,
            positionVar,
        }
    }
)

const isSingleMedia = memoize(
    [getters.tour.stopsList, supplementalMediaCarousel, point],
    (stopsList, supplementalMediaCarousel, point) => {
        return (
            stopsList.size <= 1 &&
            (!supplementalMediaCarousel.get(0) || !supplementalMediaCarousel.get(0).count) &&
            (!point.descriptions || !point.descriptions.length)
        )
    }
)

const getLocId = memoize(
    [getters.tour.locationsList, getters.tour.locationIndex, getParams],
    (locationsList, locationIndex, params) => {
        if (locationsList.length) {
            if (params.loc_id) {
                const loc = locationsList.find(
                    loc => loc.loc_id == params.loc_id || (loc.unique_id && loc.unique_id == params.loc_id)
                )
                if (loc) {
                    return loc
                }
            }

            return locationsList[locationIndex]
        }
    }
)

const getTourID = memoize(
    [getLocId, getters.tour.toursList, getParams, getLocation],
    (loc, toursList, params, location) => {
        if (toursList.length) {
            const sessionTourID = loc ? SessionStorage.getTourID(loc.loc_id) : null
            const queryTourID = location.query.tourid
            const findTour = (tourID, allowInProgress) =>
                toursList.find(tour => tour.tourid == tourID && (allowInProgress || tour.status === 'live'))
            const findTourWithLoc = (tourID, loc_id, allowInProgress) =>
                toursList.find(
                    tour =>
                        tour.tourid == tourID && tour.loc_id == loc_id && (allowInProgress || tour.status === 'live')
                )

            if (queryTourID) {
                const tour = loc ? findTourWithLoc(queryTourID, loc.loc_id, true) : findTour(queryTourID, true)

                if (tour) {
                    return tour
                }
            }

            if (sessionTourID) {
                const tourInSession = loc
                    ? findTourWithLoc(sessionTourID, loc.loc_id, false)
                    : findTour(sessionTourID, false)

                if (tourInSession) {
                    return tourInSession
                }
            }

            return (
                toursList.find(
                    tour => tour.status === 'live' && (tour.loc_id == loc.loc_id || tour.loc_id == params.loc_id)
                ) || toursList[0]
            )
        }
    }
)

/**
 * allowedAudio decides whether or not the audio should play
 * on a tour.  In the audioManager, it looks for a list of keys
 * that has the value AUDIO_PLAY, and based on the name of the key
 * it will prioritize which "audio" it should play.
 */
const allowedAudio = memoize(
    [
        mainMedia,
        audioManager,
        getters.supplementalMedia.supplementalMediaVisible,
        supplementalMedia,
        modal,
        loading,
        getters.tour.altMainMedia,
        drawer,
        getters.tour.regWelcomePrompt,
        getters.tour.welcomePrompt,
        isHybridtour,
        getters.stopDescription.isOpen,
        getters.stopDescription.mediaType,
    ],
    (
        mainMedia,
        audioManager,
        supplementalMediaVisible,
        supplementalMedia,
        modal,
        loading,
        altMainMedia,
        drawer,
        regWelcomePrompt,
        welcomePrompt,
        isHybridtour,
        isStopDescriptionOpen,
        stopDescriptionMediaType
    ) => {
        const audioManangerJSON = audioManager.toJSON()

        //returns all the keys that has the value AUDIO_PLAY
        const r = Object.keys(audioManangerJSON.state).filter(name => audioManangerJSON.state[name] === AUDIO_PLAY)

        //returns all the keys that has the value AUDIO_STOP
        const s = Object.keys(audioManangerJSON.state).filter(name => audioManangerJSON.state[name] === AUDIO_STOP)

        //returns all the keys that has the value AUDIO_LOAD
        const l = Object.keys(audioManangerJSON.state).filter(name => audioManangerJSON.state[name] === AUDIO_LOAD)

        //only stop audio when specifically asked to
        //or when welcomeprompt or registration welcomeprompt is present
        if ((s.length > 0 || regWelcomePrompt || welcomePrompt) && !loading) {
            //if it's phone
            if (MobileDetect.phone()) {
                if (isHybridtour && (regWelcomePrompt || welcomePrompt || s.find(name => name === AUDIO_TOUR))) {
                    return AUDIO_STOP
                }
            }
            if (s.find(name => name === AUDIO_TOUR) && r.find(name => name === AUDIO_TOUR)) return AUDIO_STOP
        }

        //indicates audio is loading
        if (l.length > 0) return AUDIO_LOAD

        //if loading or modal or tour_drawer is present
        if (loading || modal.get('modalStack').size > 0 || drawer.includes(TOUR_DRAWER)) {
            return null
        } else if (r.includes(AUDIO_HS)) {
            //if hotspot
            return r.find(name => [AUDIO_HS].includes(name))
        } else if (r.includes(AUDIO_TRIGGER)) {
            //if trigger
            return r.find(name => [AUDIO_TRIGGER].includes(name))
        } else if (
            (supplementalMediaVisible && supplementalMedia.mediaType === 'video') ||
            (isStopDescriptionOpen && stopDescriptionMediaType === 'video')
        ) {
            //if supp is open and video
            return r.find(name => name === AUDIO_SUPP) //then only supp
        } else if (supplementalMediaVisible) {
            //if supp is open
            return r.find(name => name === AUDIO_TOUR) //then only tour
        } else if (r.includes(AUDIO_PM)) {
            //if pm
            return r.find(name => [AUDIO_PM].includes(name))
        } else if ((r.includes(AUDIO_MAIN) && !audioManangerJSON.optin) || altMainMedia) {
            //if (main and not optin) or alt
            return r.find(name => name === AUDIO_MAIN) //then only main media
        } else if (mainMedia.mediaType != 'video') {
            return r.find(name => name !== AUDIO_MAIN)
        } else if (!drawer.includes(HIDE_DRAWER)) {
            //drawer not hidden
            return AUDIO_STOP
        }
        return r.shift()
    }
)

const allowLandscape = memoize([modal], modal => {
    let list = 0

    //check if there are modals open, if not show landscape
    if (modal.get('modalStack').size > 0) {
        //check if registration or welcome prompt is open and filter it out
        list = modal.get('modalStack').filter(prop => {
            return ![
                ModalContentTypes.REGISTRATION,
                ModalContentTypes.WELCOME_REGISTRATION,
                ModalContentTypes.WELCOME,
            ].includes(prop.content.name)
        })

        //if size is greater than 0 that means there are other modals open besides registration
        return list.size > 0
    } else {
        return true
    }
})

const shouldShowDownloadAppPrompt = memoize([getters.tour.client, location], (client, loc) => {
    const allowAbleInstIDs = [
        59848,
        59850,
        59866,
        59870,
        59877,
        59880,
        59910,
        59917,
        59949,
        59963,
        59968,
        59975,
        59985,
        59995,
        60000,
        60009,
        60011,
        60097,
        60099,
        60107,
        60109,
        60122,
        60144,
        60148,
        60161,
        60168,
        60181,
        60194,
        60195,
        60198,
        60212,
        60222,
        60234,
        60245,
        60249,
        60254,
        60302,
        60353,
        60374,
        60379,
        60421,
        60499,
        60636,
        60713,
        60719,
        60731,
        60743,
        60773,
        60810,
        60852,
        60874,
        60904,
        60906,
        60934,
        61041,
        61043,
        61115,
        61145,
        61148,
        61153,
        61178,
        61184,
        61198,
        61233,
        61316,
        61330,
        61339,
        61392,
        61397,
        61398,
        61401,
        61406,
        61421,
        61422,
        61439,
        61488,
        61489,
        61494,
        61508,
        61557,
        61581,
        61611,
        61628,
        61648,
        61680,
        61821,
        61839,
        61878,
        61985,
        62010,
        62045,
        62054,
        62056,
        62063,
        62197,
        62202,
        62204,
        62214,
        62270,
        62288,
        62346,
        62357,
        62368,
        62388,
        62474,
        62485,
        62488,
        62488,
        62521,
        62610,
        62639,
        62666,
        62675,
        62734,
        62736,
        62738,
        62803,
        62806,
        62836,
        62855,
        62890,
        62908,
        62930,
        62952,
        62966,
        63040,
        63053,
        63092,
        63157,
        63186,
        63284,
        63287,
        63290,
        63299,
        63302,
        63306,
        63393,
        63419,
        63421,
        63473,
        63520,
        63528,
        63531,
        63556,
        63560,
        63576,
        63607,
        63707,
        63709,
        63710,
        63744,
        63760,
        63764,
        63773,
        63857,
        63861,
        63883,
        63924,
        63965,
        63980,
        68706,
        71647,
        71684,
        72015,
        72297,
        72898,
        73708,
        73828,
        74654,
        75700,
        76499,
        79280,
        79391,
        80049,
        80182,
        80758,
        81025,
        82656,
        84552,
        96106,
        98676,
        102618,
        104805,
        105521,
        106765,
        109075,
        109445,
        115625,
        116231,
        116428,
        116842,
        116870,
        117819,
        117911,
        118054,
        118578,
        118901,
        119459,
        119460,
        119523,
        119528,
        119532,
        119533,
        119538,
        119547,
        119553,
        119580,
        119588,
        119590,
        119632,
        119657,
        119761,
        119939,
        119966,
        120029,
        120034,
        120062,
        120083,
        120084,
        122950,
        124317,
        131375,
        135720,
        139410,
        140399,
    ]

    const downloadPromptEnabled = !getBool(getEnv('REACT_APP_DOWNLOADPROMPT_ENABLED'))
        ? allowAbleInstIDs.includes(loc.owner_id)
        : true

    return (
        downloadPromptEnabled &&
        !client.hasShownPrompt &&
        !client.isClientApp &&
        client.isAppAvailable &&
        !UriHandler.hasQueryParam(FROM_VR) &&
        !UriHandler.hasQueryParam(WELCOME_PROMPT_HIDDEN)
    )
})

export const selectors = {
    // the ones corresponding to getters can be the first to go to /selectors!

    supplementalMediaVisible: withState(getters.supplementalMedia.supplementalMediaVisible),
    supplementalMediaInfoVisible: withState(getters.supplementalMedia.supplementalMediaInfoVisible),
    supplementalMediaPreviewVisible: withState(getters.supplementalMedia.supplementalMediaPreviewVisible),

    walkingDirection: withState(getters.navigation.walkingDirection),
    entryPoint: withState(getters.navigation.entryPoint),

    // and the rest to be sorted out in the data model and as last resort incorporated
    // in /selectors module

    drawerRaw: withState(drawerRaw),
    mainMedia: withState(mainMedia),
    mainMediaType: withState(mainMediaType),
    mainMediaPlaybackType: withState(mainMediaPlaybackType),
    currentStop: withState(currentStop),
    language: withState(language),
    navigation: withState(navigation),
    contextMenu: withState(contextMenu),
    isStopIntroPlayed: withState(isStopIntroPlayed),
    drawer: withState(drawer),
    stopCardsState: withState(stopCardsState),
    audioManager: withState(audioManager),
    loading: withState(loading),
    hasLocations: withState(hasLocations),
    isMapEnabled: withState(isMapEnabled),
    isInteractiveMap: withState(isInteractiveMap),
    isWalkingtour: withState(isWalkingtour),
    isMediatour: withState(isMediatour),
    trail: withState(trail),
    pointsList: withState(pointsList),
    trail_last_point: withState(trail_last_point),
    point: withState(point),
    trailsById: withState(trailsById),
    stopsToPoints: withState(stopsToPoints),
    pointInfo: withState(pointInfo),
    location: withState(location),
    locationName: withState(locationName),
    mediaListByType: withState(mediaListByType),
    horizontalView: withState(horizontalView),
    supplementalMedia: withState(supplementalMedia),
    tab: withState(tab, SUBJECT_HANDLER),
    mediaIndex: withState(mediaIndex),
    audioLanguage: withState(audioLanguage),
    languages: withState(languages),
    languagesAudio: withState(languagesAudio),
    selectedLanguage: withState(selectedLanguage),
    selectedAudioLanguage: withState(selectedAudioLanguage),
    locationSelectorList: withState(locationSelectorList),
    hasMultipleTrails: withState(hasMultipleTrails),
    hasMultipleStops: withState(hasMultipleStops),
    hasMultipleToursLocations: withState(hasMultipleToursLocations),
    mapTourLabel: withState(mapTourLabel),
    isFacingForward: withState(isFacingForward),
    fromVR: withState(getters.tour.fromVR),
    loadSingleStopOnly: withState(getters.tour.loadSingleStopOnly),
    trailConnections: withState(trailConnections),
    navButtons: withState(navButtons),
    endOfTourMessage: withState(endOfTourMessage),

    mapTrailPoints: withState(mapTrailPoints),
    mapStopMarkers: withState(mapStopMarkers),
    registrationForm: withState(registrationForm),
    isVrModuleEnabled: withState(isVrModuleEnabled),
    panoCoords: withState(panoCoords),

    isHybridtour: withState(isHybridtour),
    hasMainMedia: withState(hasMainMedia),
    isSingleMedia: withState(isSingleMedia),
    allowedAudio: withState(allowedAudio),
    allowLandscape: withState(allowLandscape),
    hasIntroVideoPlayed: withState(hasIntroVideoPlayed),

    supplementalMediaCarouselMediasLength: withProps(supplementalMediaCarouselMediasLength),
    supplementalMediaCarouselMediaByIndex: withProps(supplementalMediaCarouselMediaByIndex),
    isCurrentStop: withProps(isCurrentStop),
    supplementalMediaCarousel: withProps(supplementalMediaCarousel),
    getLocId: withProps(getLocId),
    getTourID: withProps(getTourID),
    audioUrl: withState(audioUrl),
    avatarUrl: withState(avatarUrl),
    isAudioButtonVisible: withState(isAudioButtonVisible),
    isSingleAudioLanguage: withState(isSingleAudioLanguage),
    shouldShowDownloadAppPrompt: withState(shouldShowDownloadAppPrompt),
}

export const getStopTrailPoint = (tour, toGet) => {
    const { points_current, trails_current } = toGet(tour)

    const trails = tour.get('trails').get('list')
    const points = tour.get('points').get('list')
    const stops = tour.get('stops').get('list')
    const trail = trails ? trails[trails_current] : {}

    const point = trail && points[trail.id] ? points[trail.id][points_current] || {} : {}

    /* Located nearest stopid to our point */
    let nearest_stop_id = point.stopid
    /*
     * Does current point stopid work?
     * Or do we have a current stop already?
     * Case when this is required: when sharing to a point that is not associated with a stop
     */
    if (nearest_stop_id == null && tour.get('stops').get('current') == null) {
        /**
         * This logic will walk around the experience to find the closest stop
         */
        let points_previous = points_current - 1
        while (nearest_stop_id == null && points_previous > 0) {
            /* Step back till we find first point with associated stop */
            const previous_point = trail && points[trail.id] ? points[trail.id][points_previous] || {} : {}
            nearest_stop_id = previous_point.stopid
            points_previous = points_previous - 1
        }
        let points_next = points_current + 1
        while (nearest_stop_id == null && points_next < points_current + 5) {
            /* Step forward a bit until we find the next point with associated stop */
            const previous_point = trail && points[trail.id] ? points[trail.id][points_next] || {} : {}
            nearest_stop_id = previous_point.stopid
            points_next = points_next + 1
        }
    }
    const foundStop = nearest_stop_id ? stops.findIndex(stop => stop.stopid === nearest_stop_id) : -1
    const stops_current = foundStop >= 0 ? foundStop : tour.get('stops').get('current')

    const stop_active = tour.stops && stops.get(tour.stops.current) ? stops.get(tour.stops.current) : {}

    const stop = stops.find(({ stopid }) => stopid === point.stopid) || {}

    const trail_last_point = trail ? (points[trail.id] || []).length == points_current + 1 : false

    const stop_changed = tour.getIn(['stops', 'current']) != stops_current
    const trail_changed = tour.getIn(['trails', 'current']) != trails_current

    return {
        points_current,
        points,
        point,
        trail_last_point,
        trails_current,
        trails,
        trail,
        stop,
        stops_current,
        stop_active,
        stop_changed,
        trail_changed,
    }
}

export const getPrev = tour => {
    const trails = tour.get('trails')
    const points = tour.get('points')
    if (points.get('current') === 0) {
        if (trails.get('current')) {
            return {
                points_current: points.get('list')[trails.get('list')[trails.get('current') - 1].id].length - 1,
                trails_current: trails.get('current') - 1,
            }
        }
        return {
            points_current: 0,
            trails_current: 0,
        }
    }
    return {
        points_current: points.get('current') - 1,
        trails_current: trails.get('current'),
    }
}

export const getIndex = index => tour => {
    const stop = tour
        .get('stops')
        .get('list')
        .get(index)
    const trails = tour.get('trails')
    const points = tour.get('points')
    return (
        (trails.get('list') || []).reduce((pair, trail, trail_index) => {
            return (points.get('list')[trail.id] || []).reduce((pair, point, point_index) => {
                if (!pair && point.stopid === stop.stopid && point.main) {
                    return {
                        points_current: point_index,
                        trails_current: trail_index,
                    }
                }
                return pair
            }, pair)
        }, null) || {}
    )
}

export const getNext = tour => {
    const trails = tour.get('trails')
    const points = tour.get('points')
    const trail = trails.get('list')[trails.get('current')]

    if (points.get('current') + 1 === (points.get('list')[trail.id] || []).length) {
        if (trails.get('current') + 1 < trails.get('list').length) {
            return {
                points_current: 0,
                trails_current: trails.get('current') + 1,
            }
        }
        return {
            points_current: 0,
            trails_current: 0,
        }
    }
    return {
        points_current: points.get('current') + 1,
        trails_current: trails.get('current'),
    }
}

export const getCurrent = tour => {
    return {
        points_current: tour.get('points').get('current'),
        trails_current: tour.get('trails').get('current'),
    }
}

export const getHotspotMedia = ({ media, locId }) => {
    const hotspotMedia = internalGetMainMedia({
        point: Object.assign({}, media, {
            id: media.item_id,
            locId: locId,
            mediaType: media.item_type,
            thumb: media.external_url,
            tinyurl: media.external_url,
            smallurl: media.external_url,
            mediumurl: media.external_url,
            largeurl: media.external_url,
        }),
    })

    return hotspotMedia
}

export const getCurrentPoint = tour => {
    const { point } = getStopTrailPoint(tour, () => {
        return {
            points_current: tour.get('points').get('current'),
            trails_current: tour.get('trails').get('current'),
        }
    })
    return point
}

export const getTourGuideAudio = (point, language, orientation) => {
    const { descriptions } = point

    return ((descriptions[language] || {})[orientation] || {}).audio_url
}

export const applyExperienceUpdated = (apiData, experience_updated) => {
    return pickBy(
        {
            ...apiData,
            experience_updated,
        },
        negate(isUndefined)
    )
}

export const properCase = word => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : null)

export const delay = duration => new Promise(resolve => setTimeout(resolve, duration))

export const imgLoaded = imgElement =>
    typeof imgElement === 'undefined' || null == imgElement || (imgElement.complete && imgElement.naturalHeight !== 0)
